import { apiFetch } from './client';
import { formatBucketLabel, toDashboardQuery } from './analyticsParams';
import {
  mockCategories,
  mockChannels,
  mockFulfillmentTimeseries,
  mockGst,
  mockPayments,
  mockReturns,
  mockReturnsTimeseries,
  mockSummary,
  mockTimeseries,
  mockTopProducts,
} from '../pages/Analytics/mockData';

const USE_MOCK = process.env.REACT_APP_ANALYTICS_MOCK === '1';

function qs(params) {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : '';
}

async function withMock(request, mockFn) {
  if (USE_MOCK) return mockFn();
  return request();
}

/** Map API timeseries series+previous → chart buckets (pos/delivery lowercase keys). */
function normalizeTimeseries(data) {
  if (!data) return data;
  if (Array.isArray(data.buckets) && data.buckets.length) return data;

  const series = data.series || [];
  const previous = data.previous || [];
  // previous[] uses offset (index into previous window), may be shorter/empty — align by offset.
  const prevByOffset = new Map();
  previous.forEach((row, i) => {
    const offset = typeof row?.offset === 'number' ? row.offset : i;
    prevByOffset.set(offset, row);
  });

  const buckets = series.map((row, i) => {
    const prev = prevByOffset.get(i);
    // Missing channel keys → 0 (defense); BE zeros both on ALL buckets it emits.
    const pos = Number(row.POS ?? row.pos ?? 0);
    const delivery = Number(row.DELIVERY ?? row.delivery ?? 0);
    const rawT = row.t || row.label || String(i);
    const bucket = {
      label: formatBucketLabel(rawT),
      pos,
      delivery,
    };
    if (prev) {
      const pPos = Number(prev.POS ?? prev.pos ?? 0);
      const pDel = Number(prev.DELIVERY ?? prev.delivery ?? 0);
      bucket.previous = pPos + pDel;
      bucket.previous_pos = pPos;
      bucket.previous_delivery = pDel;
    }
    return bucket;
  });
  return { ...data, buckets };
}

function normalizeCountSeries(data) {
  if (!data) return data;
  if (Array.isArray(data.buckets) && data.buckets.length) return data;
  const series = data.series || [];
  const buckets = series.map((row, i) => {
    const rawT = row.t || row.label || String(i);
    return {
      label: formatBucketLabel(rawT),
      ...row,
    };
  });
  return { ...data, buckets };
}

function normalizeChannels(data) {
  if (!data) return data;
  const channels = (data.channels || []).map((c) => ({
    ...c,
    channel: String(c.channel || '').toLowerCase(),
  }));
  return { ...data, channels, total_revenue: data.total_revenue };
}

const PAYMENT_METHODS = new Set(['UPI', 'CASH', 'CARD', 'COD', 'CREDIT']);

function normalizePayments(data) {
  if (!data) return data;
  const methods = (data.methods || [])
    .map((m) => ({ ...m, method: String(m.method || '').toUpperCase() }))
    .filter((m) => PAYMENT_METHODS.has(m.method) && (m.collected || 0) > 0);
  return {
    ...data,
    // collected_total is always present (incl. 0); prefer it — do not sum methods.
    collected_total: data.collected_total != null ? Number(data.collected_total) : 0,
    methods,
    payments: methods,
  };
}

/** Contract: list is always `items`; profit field is always `gross_profit`. */
function normalizeTopList(data) {
  if (!data) return data;
  const items = data.items || [];
  return { ...data, items };
}

function normalizeGst(data) {
  if (!data) return data;
  return { ...data, totals: data.totals };
}

function normalizeReturnsTiles(data) {
  if (!data) return data;
  return {
    ...data,
    credits_issued: data.credits_issued,
    credit_count: data.credit_count,
    exchange_count: data.exchange_count,
    failed_pickup_count: data.failed_pickup_count,
  };
}

export function getDashboardSummary({ from, to, channel, compare, period }, token) {
  return withMock(
    () =>
      apiFetch(`/store/dashboard/summary${qs(toDashboardQuery({ from, to, channel, compare, period }))}`, {
        token,
      }),
    () => mockSummary({ from, to, compare })
  );
}

export function getDashboardTimeseries(
  { from, to, channel, compare, granularity, metric, period },
  token
) {
  return withMock(
    async () => {
      const data = await apiFetch(
        `/store/dashboard/timeseries${qs(
          toDashboardQuery({ from, to, channel, compare, period, granularity, metric })
        )}`,
        { token }
      );
      return normalizeTimeseries(data);
    },
    () => normalizeTimeseries(mockTimeseries({ from, to, channel, granularity, metric, compare }))
  );
}

export function getDashboardChannels({ from, to, channel, period }, token) {
  return withMock(
    async () => normalizeChannels(
      await apiFetch(`/store/dashboard/channels${qs(toDashboardQuery({ from, to, channel, period }))}`, {
        token,
      })
    ),
    () => normalizeChannels(mockChannels())
  );
}

export function getDashboardPayments({ from, to, channel, period }, token) {
  return withMock(
    async () => normalizePayments(
      await apiFetch(`/store/dashboard/payments${qs(toDashboardQuery({ from, to, channel, period }))}`, {
        token,
      })
    ),
    () => normalizePayments(mockPayments())
  );
}

export function getDashboardTopProducts({ from, to, channel, metric, period, limit = 20 }, token) {
  return withMock(
    async () => normalizeTopList(
      await apiFetch(
        `/store/dashboard/top-products${qs(
          toDashboardQuery({ from, to, channel, period, metric, limit })
        )}`,
        { token }
      )
    ),
    () => normalizeTopList(mockTopProducts({ metric }))
  );
}

export function getDashboardCategories({ from, to, channel, metric, period, limit = 5 }, token) {
  return withMock(
    async () => normalizeTopList(
      await apiFetch(
        `/store/dashboard/categories${qs(
          toDashboardQuery({ from, to, channel, period, metric, limit })
        )}`,
        { token }
      )
    ),
    () => normalizeTopList(mockCategories({ metric, limit }))
  );
}

export function getDashboardGst({ from, to, channel, period }, token) {
  return withMock(
    async () => normalizeGst(
      await apiFetch(`/store/dashboard/gst${qs(toDashboardQuery({ from, to, channel, period }))}`, {
        token,
      })
    ),
    () => normalizeGst(mockGst())
  );
}

export function getDashboardReturns({ from, to, channel, period }, token) {
  return withMock(
    async () => normalizeReturnsTiles(
      await apiFetch(`/store/dashboard/returns${qs(toDashboardQuery({ from, to, channel, period }))}`, {
        token,
      })
    ),
    () => normalizeReturnsTiles(mockReturns())
  );
}

/** Returns & exchange chart — GET /store/dashboard/returns/timeseries */
export function getDashboardReturnsTimeseries(
  { from, to, channel, granularity, period },
  token
) {
  return withMock(
    async () => normalizeCountSeries(
      await apiFetch(
        `/store/dashboard/returns/timeseries${qs(
          toDashboardQuery({ from, to, channel, period, granularity })
        )}`,
        { token }
      )
    ),
    () => normalizeCountSeries(mockReturnsTimeseries({ from, to, channel, granularity }))
  );
}

/** Scheduling / fulfillment — GET /store/dashboard/fulfillment/timeseries */
export function getDashboardFulfillmentTimeseries(
  { from, to, channel, granularity, period },
  token
) {
  return withMock(
    async () => normalizeCountSeries(
      await apiFetch(
        `/store/dashboard/fulfillment/timeseries${qs(
          toDashboardQuery({ from, to, channel, period, granularity })
        )}`,
        { token }
      )
    ),
    () => normalizeCountSeries(mockFulfillmentTimeseries({ from, to, channel, granularity }))
  );
}

/** @deprecated Use getDashboardReturnsTimeseries */
export function getDashboardReturnsRefunds(args, token) {
  return getDashboardReturnsTimeseries(args, token);
}

/** @deprecated Use getDashboardFulfillmentTimeseries */
export function getDashboardScheduling(args, token) {
  return getDashboardFulfillmentTimeseries(args, token);
}
